import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FileText, Loader2, Pencil, XCircle } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { binderyApi, type ThesisPrintRequest } from "@/lib/api/bindery";
import { ThesisStatusBadge } from "@/components/bindery/BinderyStatusBadge";
import { ThesisStatusStepper } from "@/components/bindery/ThesisStatusStepper";
import {
  BINDING_TYPE_LABELS,
  DEGREE_LEVEL_LABELS,
  formatDate,
  formatMoney,
} from "@/pages/bindery/binderyFormatters";
import { PatronPaymentPanel } from "./PatronPaymentPanel";

const PATRON_CAN_REVISE: ThesisPrintRequest["status"][] = ["SUBMITTED"];

export default function MyThesisDetailPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [req, setReq] = useState<ThesisPrintRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setReq(await binderyApi.thesis.mineById(id));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to load thesis request"));
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    reload();
  }, [reload]);

  const cancel = async () => {
    if (!req || !window.confirm("Cancel this request?")) return;
    setCancelling(true);
    try {
      const next = await binderyApi.thesis.cancelMine(req.id);
      setReq(next);
      toast.success("Request cancelled");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to cancel request"));
    } finally {
      setCancelling(false);
    }
  };

  if (loading || !req) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title={req.requestNumber}
        description={req.title}
        breadcrumbItems={[
          { label: "My thesis requests", href: "/my/thesis" },
          { label: req.title },
        ]}
        showSearch={false}
        titleSize="md"
        actions={
          PATRON_CAN_REVISE.includes(req.status) ? (
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to={`/my/thesis/${req.id}/edit`}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </Link>
              </Button>
              <Button variant="destructive" disabled={cancelling} onClick={cancel}>
                {cancelling ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                Cancel
              </Button>
            </div>
          ) : undefined
        }
      />

      <Card>
        <CardContent className="pt-6">
          <ThesisStatusStepper status={req.status} />
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-4">
        <InfoTile label="Status" value={<ThesisStatusBadge status={req.status} />} />
        <InfoTile label="Degree" value={DEGREE_LEVEL_LABELS[req.degreeLevel]} />
        <InfoTile label="Binding" value={BINDING_TYPE_LABELS[req.bindingType]} />
        <InfoTile label="Submitted" value={formatDate(req.submittedAt)} />
        <InfoTile label="Pages × Copies" value={`${req.pageCount} × ${req.copies}`} />
        <InfoTile label="Color pages" value={String(req.colorPages)} />
        <InfoTile label="Ready" value={formatDate(req.readyAt)} />
        <InfoTile label="Cost" value={formatMoney(req.cost)} />
      </div>

      <PatronPaymentPanel req={req} />

      {req.rejectionReason && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-semibold">Your request was rejected</p>
          <p className="mt-1">{req.rejectionReason}</p>
        </div>
      )}

      {req.status === "READY" && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          Your bound copies are ready for collection at the library.
        </div>
      )}

      {req.formattingNotes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Formatting notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm text-slate-700">{req.formattingNotes}</p>
          </CardContent>
        </Card>
      )}

      {req.fileUrl && (
        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <FileText className="h-4 w-4 text-slate-500" />
              Manuscript file
            </div>
            <a
              href={req.fileUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-primary hover:underline"
            >
              View
            </a>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

function InfoTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <p className="text-xs text-slate-500">{label}</p>
      <div className="mt-1 text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}
